// datoteka: src/Components/UserDashboard/FavoriteAds.js

import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Paper, Stack, IconButton, Box, Tooltip, Select, MenuItem, FormControl, InputLabel, Grid, Typography, TextField, Button } from '@mui/material';
import { Delete, Visibility } from "@mui/icons-material";
import { AuthContext } from "../../AuthContext";
import { getImageUrl } from "../../utils/imageUtils";
import { generateSlug } from "../../utils/slug";
import useMediaQuery from "../../hooks/useMediaQuery";
import MobileFavoriteAdCard from "./MobileFavoriteAdCard"; // Uvoz nove komponente
import Swal from "sweetalert2";
import "./FavoriteAds.css";

// Helper function to determine the background color based on item status
const getStatusClass = (status) => {
    switch (status) {
        case "izbrisano":
            return "red";
        case "na voljo":
            return "#463CE5";
        case "prodano":
            return "#FF4367";
        case "potečeno":
            return "orange";
        default:
            return "#ccc";
    }
};

// Pagination settings
const paginationModel = { page: 0, pageSize: 5 };

const FavoriteAds = () => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { user, loading } = useContext(AuthContext); // Uporabnik iz AuthContexta
    const [items, setAds] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const navigate = useNavigate();

    // Stanja za filtre, sortiranje, iskanje in mobilno izbiro
    const [filterStatus, setFilterStatus] = useState('vsi');
    const [sortBy, setSortBy] = useState('datum_desc');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSelectMode, setIsSelectMode] = useState(false);
    const [mobileSelectedIds, setMobileSelectedIds] = useState([]);

    // Fetch user favorite items
    useEffect(() => {
        const fetchData = async () => {
            if (!user) {
                console.warn("User is not logged in.");
                setAds([]);
                return;
            }

            try {
                const adsResponse = await fetch(`${process.env.REACT_APP_API_URL}/user/${user.id}/favorites`, {
                    method: "GET",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (!adsResponse.ok) {
                    const responseText = await adsResponse.text();
                    throw new Error(`Error fetching items: ${adsResponse.status} - ${responseText}`);
                }

                const adsData = await adsResponse.json(); // Parsiramo JSON
                setAds(adsData); // Nastavimo oglase
            } catch (error) {
                console.error("Error fetching data:", error);
                setAds([]);
            }
        };

        if (!loading) { // Počakamo, da se preveri prijava
            fetchData();
        }
    }, [user, loading]);

    // Filtriranje in sortiranje oglasov za mobilni pogled
    const filteredAndSortedAds = React.useMemo(() => {
        let processedAds = [...items];
        if (searchTerm) {
            processedAds = processedAds.filter(item => item.title.toLowerCase().includes(searchTerm.toLowerCase()));
        }
        if (filterStatus !== 'vsi') {
            processedAds = processedAds.filter(item => item.status === filterStatus);
        }
        switch (sortBy) {
            case 'datum_asc': processedAds.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); break;
            case 'cena_asc': processedAds.sort((a, b) => a.price - b.price); break;
            case 'cena_desc': processedAds.sort((a, b) => b.price - a.price); break;
            default: processedAds.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); break;
        }
        return processedAds;
    }, [items, filterStatus, sortBy, searchTerm]);

    // Univerzalna funkcija za brisanje (masovno ali posamezno)
    const handleDeleteFavorites = async (idsToDelete) => {
        const finalIds = idsToDelete || selectedIds;

        if (finalIds.length === 0) {
            Swal.fire("Ni izbranih oglasov!", "", "warning");
            return;
        }

        const result = await Swal.fire({
            title: `Ali ste prepričani?`,
            text: `Želite odstraniti ${finalIds.length} oglas(ov) iz priljubljenih?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Da, odstrani",
            cancelButtonText: "Prekliči"
        });

        if (result.isConfirmed) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/user/favorites`, {
                    method: "DELETE",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ listing_id: finalIds }),
                });

                if (!response.ok) throw new Error("Napaka pri odstranjevanju oglasov.");

                setAds(prevAds => prevAds.filter(item => !finalIds.includes(item.id)));
                Swal.fire("Odstranjeno!", "Oglasi so bili uspešno odstranjeni iz priljubljenih.", "success");

                setSelectedIds([]);
                setMobileSelectedIds([]);
                setIsSelectMode(false);
            } catch (error) {
                Swal.fire("Napaka", error.message, "error");
            }
        }
    };

    // Handler za mobilno izbiro
    const handleMobileSelectToggle = (adId) => {
        setMobileSelectedIds(prev => prev.includes(adId) ? prev.filter(id => id !== adId) : [...prev, adId]);
    };

    // Handle row selection changes
    const handleSelectionChange = (selection) => {
        setSelectedIds(selection);
    };

    const handleDeleteFavoriteAdClick = async (row) => {
        if (!row.id) {
            console.error("item ID is missing.");
            return;
        }

        const result = await Swal.fire({
            title: `Ali ste prepričani, da želite izbrisati priljubljen oglas "${row.title}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Izbriši',
            cancelButtonText: 'Prekliči',
            reverseButtons: true
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/user/${user.id}/favorites/${row.id}`, {
                method: "DELETE",
                credentials: "include",
            });

            if (!response.ok) {
                const errorMessage = await response.text();
                throw new Error(`Error deleting item: ${response.status} - ${errorMessage}`);
            }

            // Če je uspešno, posodobi stanje in odstrani oglas
            setAds((prevAds) => prevAds.filter((item) => item.id !== row.id));
            Swal.fire({
                title: 'Oglas uspešno izbrisan!',
                icon: 'success',
            });
            console.log(`item "${row.title}" successfully deleted.`);
        } catch (error) {
            console.error("Failed to delete item:", error);
            Swal.fire({
                title: `Napaka pri brisanju oglasa: ${error.message}`,
                icon: 'error',
            });
        }
    };

    // Define column structure for the DataGrid
    const columns = [
        {
            field: "image",
            headerName: "Slika",
            width: 100, // Prilagodi širino stolpca, če je potrebno (npr. 110, da je prostor za padding)
            renderCell: (params) => {
                const { images: rowImages, title: rowTitle, id: rowId } = params.row;
                const slug = generateSlug(rowTitle);

                const primaryImage = rowImages?.find(img => img.is_primary) || rowImages?.[0];
                const imageKey = primaryImage?.image_url;
                const imageUrl = getImageUrl(imageKey, 'card', { text: "Oglas" });

                // Stili za kontejner slike
                const imageContainerStyles = {
                    width: 85,
                    height: 60,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    borderRadius: "4px",
                    cursor: 'pointer', // Dodaj cursor pointer na celoten Box, če je klikabilen
                    backgroundColor: imageKey ? 'transparent' : '#f0f0f0', // Placeholder barva
                    border: !imageKey ? '1px dashed #ccc' : 'none', // Placeholder obroba
                };

                // Stili za sliko
                const imageStyles = {
                    display: 'block', // Odstrani morebitni prostor pod sliko
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "cover",
                };

                return (
                    <Box sx={imageContainerStyles} onClick={() => navigate(`/oglas/${slug}/${rowId}`)}>
                        {imageKey ? ( // Pogojno renderiraj img samo, če imamo ključ
                            <img
                                src={imageUrl}
                                alt={rowTitle || "Slika oglasa"}
                                style={imageStyles}
                                loading="lazy"
                            />
                        ) : (
                            // Lahko prikažeš ikono ali tekst, če ni slike, namesto samo sivega ozadja
                            // Npr. <ImageIcon sx={{ color: '#bdbdbd' }} /> ali preprost tekst
                            null // Ali pa pustiš, da se prikaže samo Box s placeholder stilom
                        )}
                    </Box>
                );
            },
        },
        {
            field: "title",
            headerName: "Naziv",
            width: 200,
            renderCell: (params) => {
                // === SPREMEMBA 3: Generiramo slug iz naslova ===
                const slug = generateSlug(params.value);
                return (
                    // === SPREMEMBA 4: Uporabimo novo pot za navigacijo ===
                    <span
                        style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px", cursor: "pointer" }}
                        onClick={() => navigate(`/oglas/${slug}/${params.row.id}`)}
                    >
                        {params.value}
                    </span>
                );
            },
        },
        {
            field: "price",
            headerName: "Cena",
            width: 100,
            renderCell: (params) => (
                <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px" }}>
                    {params.value} €
                </span>
            ),
        },
        {
            field: "category",
            headerName: "Kategorija",
            width: 150,
            renderCell: (params) => (
                <span style={{ fontFamily: "'Roboto', sans-serif", fontSize: "16px" }}>
                    {params.value?.name}
                </span>
            ),
        },
        {
            field: "status",
            headerName: "Status",
            width: 120,
            renderCell: (params) => (
                <span
                    style={{
                        display: "inline-block",
                        width: "70px",
                        height: "25px",
                        lineHeight: "25px",
                        borderRadius: "25px",
                        textAlign: "center",
                        color: "#ffffff",
                        backgroundColor: getStatusClass(params.value),
                        fontFamily: "'Roboto', sans-serif",
                        fontSize: "16px",
                    }}
                >
                    {params.value}
                </span>
            ),
        },
        {
            field: "actions",
            headerName: "Akcije",
            width: 170,
            renderCell: ({ row }) => {
                // === SPREMEMBA 5: Generiramo slug iz naslova ===
                const slug = generateSlug(row.title);
                return (
                    <Box>
                        <Tooltip title="Poglej oglas" arrow>
                            {/* === SPREMEMBA 6: Uporabimo novo pot za navigacijo === */}
                            <IconButton onClick={() => navigate(`/oglas/${slug}/${row.id}`)} aria-label="ogled">
                                <Visibility />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Izbriši oglas" arrow>
                            {/* Klic te funkcije ne vsebuje navigacije, zato ostane enak */}
                            <IconButton onClick={() => handleDeleteFavoriteAdClick(row)} sx={{ color: "red" }} aria-label="izbris">
                                <Delete />
                            </IconButton>
                        </Tooltip>
                    </Box>
                );
            },
        },
    ];

    return (
        <div className="favorite_items">
            <h2>Priljubljeni oglasi</h2>
            <hr className="hr_favorite_items" />

            {/* Kontrole za mobilni pogled */}
            {isMobile && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" component="div">
                            Moji oglasi
                        </Typography>
                        <Button variant="outlined" size="small" onClick={() => { setIsSelectMode(!isSelectMode); setMobileSelectedIds([]); }}>
                            {isSelectMode ? 'Prekliči' : 'Izberi'}
                        </Button>
                    </Box>
                    <TextField fullWidth label="Išči po nazivu..." variant="outlined" size="small" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                                    <MenuItem value="vsi">Vsi</MenuItem>
                                    <MenuItem value="na voljo">Na voljo</MenuItem>
                                    <MenuItem value="prodano">Prodano</MenuItem>
                                    <MenuItem value="potečeno">Potečeno</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Razvrsti po</InputLabel>
                                <Select value={sortBy} label="Razvrsti po" onChange={(e) => setSortBy(e.target.value)}>
                                    <MenuItem value="datum_desc">Najnovejši</MenuItem>
                                    <MenuItem value="datum_asc">Najstarejši</MenuItem>
                                    <MenuItem value="cena_desc">Cena (višja)</MenuItem>
                                    <MenuItem value="cena_asc">Cena (nižja)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* Prikaz za namizje (DataGrid) ali mobilne naprave (kartice) */}
            {isMobile ? (
                <Box>
                    {filteredAndSortedAds.length > 0 ? (
                        filteredAndSortedAds.map((item) => (
                            <MobileFavoriteAdCard
                                key={item.id}
                                item={item}
                                onDelete={() => handleDeleteFavorites([item.id])}
                                navigateToAd={() => !isSelectMode && navigate(`/oglas/${generateSlug(item.title)}/${item.id}`)}
                                getImageUrl={getImageUrl}
                                getStatusClass={getStatusClass}
                                isSelected={mobileSelectedIds.includes(item.id)}
                                onSelectToggle={() => isSelectMode && handleMobileSelectToggle(item.id)}
                            />
                        ))
                    ) : (
                        <Typography sx={{ textAlign: 'center', mt: 4 }}>Ni najdenih oglasov.</Typography>
                    )}
                </Box>
            ) : (
                <Paper sx={{ height: "auto", width: "100%" }}>
                    {selectedIds.length > 0 && (
                        <Stack direction="row" spacing={2} sx={{ mb: 2, p: 1 }}>
                            <Button variant="contained" color="error" onClick={() => handleDeleteFavorites()}>
                                Odstrani izbrane ({selectedIds.length})
                            </Button>
                        </Stack>
                    )}
                    <DataGrid
                        rows={items}
                        columns={columns}
                        initialState={{ pagination: { paginationModel } }}
                        disableSelectionOnClick
                        disableRowSelectionOnClick
                        checkboxSelection
                        onRowSelectionModelChange={handleSelectionChange}
                        pageSize={5}
                        pageSizeOptions={[5, 10, 100]}
                        getRowHeight={() => 75}
                        sx={{
                            border: 0,
                            "& .MuiDataGrid-columnHeaders": {
                                fontFamily: "'Roboto', sans-serif",
                                fontSize: "16px",
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: "bold",
                            },
                        }}
                    />
                </Paper>
            )}

            {/* Plavajoča vrstica za masovne akcije na mobilnih napravah */}
            {isMobile && isSelectMode && mobileSelectedIds.length > 0 && (
                <Paper elevation={3} sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, p: 2, display: 'flex', justifyContent: 'center', zIndex: 1000 }}>
                    <Button variant="contained" color="error" onClick={() => handleDeleteFavorites(mobileSelectedIds)}>
                        Odstrani izbrane ({mobileSelectedIds.length})
                    </Button>
                </Paper>
            )}
        </div>
    );
};

export default FavoriteAds;